import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAvailableCities,
  getCities,
  updateAvailableCity,
} from "../../services/cityService";
import { City } from "../../types/city.types";

interface CityState {
  availableCities: City[];
  selectedCity: City;
  citySearchQuery: string;
  latitude: number | null;
  longitude: number | null;
  activeCities: City[];
}
const initialState: CityState = {
  availableCities: [],
  selectedCity: {} as City,
  citySearchQuery: "",
  latitude: null,
  longitude: null,
  activeCities: [],
};

export const fetchCities = createAsyncThunk(
  "cities/fetch",
  async (
    {
      search,
      lat,
      lng,
      selectedCity,
    }: { search: string; lat: number; lng: number; selectedCity: City },
    { rejectWithValue }
  ) => {
    const { data, error } = (await getCities(
      search,
      lat,
      lng,
      selectedCity
    )) as {
      data: { items: City[]; selectedCity?: City };
      error: any;
    };

    if (error) return rejectWithValue(error);

    return {
      availableCities: data.items,
      selectedCity: data?.selectedCity || selectedCity || {},
    };
  }
);

export const fetchAvailableCities = createAsyncThunk(
  "availableCities/fetch",
  async (_, { rejectWithValue }) => {
    const { data, error } = (await getAvailableCities()) as {
      data: { items: City[]; count?: number };
      error: any;
    };

    if (error) return rejectWithValue(error);

    return data;
  }
);

export const updateCityAvailability = createAsyncThunk(
  "availableCities/update",
  async (
    { cityID, isAvailable }: { cityID: string; isAvailable: boolean },
    { rejectWithValue }
  ) => {
    const { data, error } = (await updateAvailableCity(
      cityID,
      isAvailable
    )) as {
      data: { city: City; message?: string };
      error: any;
    };

    if (error) return rejectWithValue(error);

    return data;
  }
);

export const citySlice = createSlice({
  name: "city",
  initialState,
  reducers: {
    setCitySearchQuery: (state, action) => {
      state.citySearchQuery = action.payload;
    },
    setSelectedCity: (state, action) => {
      state.selectedCity = action.payload;
    },
    setCoordinates: (
      state,
      action: { payload: { latitude: number; longitude: number } }
    ) => {
      state.latitude = action.payload.latitude;
      state.longitude = action.payload.longitude;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCities.fulfilled, (state, action) => {
      state.availableCities = action.payload.availableCities;
      state.selectedCity = action.payload.selectedCity as City;
    });
    builder.addCase(fetchAvailableCities.fulfilled, (state, action) => {
      state.activeCities = action.payload.items;
    });
    builder.addCase(updateCityAvailability.fulfilled, (state, action) => {
      const updatedCity = action.payload.city;

      const availableIndex = state.availableCities.findIndex(
        (c) => c._id === updatedCity._id
      );

      if (availableIndex !== -1) {
        state.availableCities[availableIndex] = updatedCity;
      } else {
        state.availableCities.push(updatedCity);
      }

      const activeIndex = state.activeCities.findIndex(
        (c) => c._id === updatedCity._id
      );

      if (updatedCity.isAvailable) {
        if (activeIndex === -1) {
          state.activeCities.push(updatedCity);
        } else {
          state.activeCities[activeIndex] = updatedCity;
        }
      } else {
        if (activeIndex !== -1) {
          state.activeCities.splice(activeIndex, 1);
        }
      }
    });
  },
});

export const { setCitySearchQuery, setSelectedCity, setCoordinates } =
  citySlice.actions;
export default citySlice.reducer;
